import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { auth, db, functions } from "../../firebase";
import DashboardLayout from "../dashboard/_components/DashboardLayout";
import { doc, getDoc, updateDoc, collection, addDoc, getDocs } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import {
  User,
  Mail,
  Phone,
  Calendar,
  MapPin,
  Briefcase,
  CreditCard,
  FileText,
  ArrowLeft,
  IndianRupee,
  Home,
  Globe,
  Hash,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Download,
  MessageSquare,
  History,
  Building,
  TrendingUp,
  Percent,
  DollarSign,
  X,
  Eye,
  Image as ImageIcon,
  RefreshCw,
  Video,
  Copy,
  Check,
  ExternalLink,
  Coins,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import logo from "../../assets/logod.png";

export default function PersonalLoanDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [loan, setLoan] = useState(null);
  const [enachApplications, setEnachApplications] = useState([]);
  const [enachLoading, setEnachLoading] = useState(false);
  const [enachError, setEnachError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [messageModal, setMessageModal] = useState(false);
  const [message, setMessage] = useState("");
  const [subject, setSubject] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    action: "",
    status: "",
  });
  const [editingAmount, setEditingAmount] = useState(false);
  const [editedLoanAmount, setEditedLoanAmount] = useState("");
  const [savingAmount, setSavingAmount] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const [callDate, setCallDate] = useState("");
  const [callTimeSlot, setCallTimeSlot] = useState("");
  const [schedulingCall, setSchedulingCall] = useState(false);
  const [callResult, setCallResult] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((u) => {
      if (u) {
        setUser(u);
      } else {
        navigate("/login");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const fetchEnachApplications = async (loanData) => {
    setEnachLoading(true);
    setEnachError(false);
    try {
      const snapshot = await getDocs(collection(db, "enachApplications"));
      const loanIdentifiers = [
        loanData.id,
        loanData.applicationId,
        loanData.loanId,
        loanData.userId,
        loanData.customerId,
        loanData.email,
        loanData.phone,
        loanData.phoneNumber,
        loanData.mobileNumber,
      ]
        .filter(Boolean)
        .map((value) => String(value).trim().toLowerCase());
      const identifierFields = new Set([
        "loanid",
        "personalloanid",
        "applicationid",
        "loanapplicationid",
        "userid",
        "customerid",
        "email",
        "phone",
        "phonenumber",
        "mobilenumber",
      ]);

      const matchedApplications = snapshot.docs
        .map((applicationDoc) => ({
          id: applicationDoc.id,
          ...applicationDoc.data(),
        }))
        .filter((application) =>
          Object.entries(application).some(([key, value]) => {
            const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]/g, "");
            return (
              identifierFields.has(normalizedKey) &&
              value != null &&
              loanIdentifiers.includes(String(value).trim().toLowerCase())
            );
          }),
        );

      setEnachApplications(matchedApplications);
    } catch (error) {
      console.error("Failed to fetch eNACH applications:", error);
      setEnachApplications([]);
      setEnachError(true);
    } finally {
      setEnachLoading(false);
    }
  };

  const fetchLoan = async () => {
    setLoading(true);
    try {
      const loanDoc = await getDoc(doc(db, "personalLoan", id));
      if (loanDoc.exists()) {
        const loanData = { id: loanDoc.id, ...loanDoc.data() };
        setLoan(loanData);
        fetchEnachApplications(loanData);
      } else {
        navigate("/personal-loans");
      }
    } catch (error) {
      navigate("/personal-loans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && id) {
      fetchLoan();
    }
  }, [user, id, navigate]);

  const handleSaveLoanAmount = async () => {
    if (!editedLoanAmount || isNaN(editedLoanAmount)) {
      toast.error("Please enter a valid loan amount");
      return;
    }

    try {
      setSavingAmount(true);

      await updateDoc(doc(db, "personalLoan", id), {
        loanAmount: Number(editedLoanAmount),
        updatedAt: new Date(),
        updatedBy: user.uid,
      });

      // Update local state (NO refetch)
      setLoan((prev) => ({
        ...prev,
        loanAmount: Number(editedLoanAmount),
        updatedAt: new Date(),
      }));

      setEditingAmount(false);
      toast.success("Loan amount updated successfully");
    } catch (err) {
      console.error(err);
      toast.error("Failed to update loan amount");
    } finally {
      setSavingAmount(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    try {
      if (timestamp.toDate) {
        return timestamp.toDate().toLocaleDateString("en-IN", {
          day: "numeric",
          month: "long",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
      return new Date(timestamp).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "N/A";
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
      approved: "bg-green-100 text-green-800 border-green-200",
      rejected: "bg-red-100 text-red-800 border-red-200",
      "under-review": "bg-blue-100 text-blue-800 border-blue-200",
      disbursed: "bg-purple-100 text-purple-800 border-purple-200",
      completed: "bg-gray-100 text-gray-800 border-gray-200",
    };
    return colors[status] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: Clock,
      approved: CheckCircle,
      rejected: XCircle,
      "under-review": AlertCircle,
      disbursed: DollarSign,
      completed: CheckCircle,
    };
    return icons[status] || Clock;
  };

  const handleStatusUpdate = (newStatus) => {
    if (!loan) return;

    let actionText = "";
    if (newStatus === "under-review") {
      actionText = "move this loan application to review";
    } else if (newStatus === "approved") {
      actionText = "approve this loan application";
    } else if (newStatus === "rejected") {
      actionText = "reject this loan application";
    }

    setConfirmModal({ show: true, action: actionText, status: newStatus });
  };

  const confirmStatusUpdate = async () => {
    const newStatus = confirmModal.status;
    setConfirmModal({ show: false, action: "", status: "" });

    setUpdating(true);
    try {
      // Update loan status
      await updateDoc(doc(db, "personalLoan", id), {
        status: newStatus,
        updatedAt: new Date(),
        updatedBy: user.uid,
      });

      setLoan({ ...loan, status: newStatus, updatedAt: new Date() });

      // Send email notification to customer
      if (loan.email) {
        try {
          const statusMessages = {
            "under-review": {
              subject: "Your Loan Application is Under Review",
              message: `Dear ${
                loan.fullName
              },\n\nYour personal loan application for ${formatCurrency(
                loan.loanAmount,
              )} has been received and is currently under review by our team.

We will notify you once the review is complete.

Application Details:
- Loan Amount: ${formatCurrency(
                loan.loanAmount,
              )}\n- Loan Purpose: ${
                loan.loanPurpose || "N/A"
              }
- Status: Under Review

If you have any questions, please feel free to contact us.

Best regards,
Aadhar Finance Team`,
            },
            approved: {
              subject: "🎉 Congratulations! Your Loan Application is Approved",
              message: `Dear ${
                loan.fullName
              },

Congratulations! We are pleased to inform you that your personal loan application has been APPROVED.

Approved Loan Details:
- Loan Amount: ${formatCurrency(
                loan.loanAmount,
              )}\n- Loan Purpose: ${
                loan.loanPurpose || "N/A"
              }
- Status: Approved

Our team will contact you shortly to complete the disbursement process.

Thank you for choosing Aadhar Finance!

Best regards,
Aadhar Finance Team`,
            },
            rejected: {
              subject: "Update on Your Loan Application",
              message: `Dear ${
                loan.fullName
              },

Thank you for your interest in Aadhar Finance.

After careful review of your personal loan application for ${formatCurrency(
                loan.loanAmount,
              )}, we regret to inform you that we are unable to approve your application at this time.

This decision was made based on our current lending criteria and policies.

We encourage you to reapply in the future when your circumstances may have changed.

If you have any questions, please don't hesitate to contact us.

Best regards,
Aadhar Finance Team`,
            },
          };

          const emailContent = statusMessages[newStatus];
          if (emailContent) {
            const mailRef = collection(db, "mail");
            await addDoc(mailRef, {
              to: loan.email,
              message: {
                subject: emailContent.subject,
                text: emailContent.message,
                html: `
                  <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f9fafb;">
                    <div style="background-color: white; padding: 30px; border-radius: 10px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                      <h2 style="color: ${
                        newStatus === "approved"
                          ? "#10b981"
                          : newStatus === "rejected"
                            ? "#ef4444"
                            : "#3b82f6"
                      }; margin-bottom: 20px;">
                        ${emailContent.subject}
                      </h2>
                      <p style="color: #374151; line-height: 1.6; white-space: pre-wrap; font-size: 15px;">
                        ${emailContent.message.replace(/\n/g, "<br>")}
                      </p>
                      <hr style="border: 1px solid #e5e7eb; margin: 30px 0;" />
                      <p style="color: #6b7280; font-size: 12px; margin: 0;">
                        This is an automated notification from Aadhar Finance. Please do not reply to this email.
                      </p>
                    </div>
                  </div>
                `,
              },
              createdAt: new Date(),
              sentBy: user.email,
              customerId: loan.uid || id,
              customerName: loan.fullName,
              loanId: id,
              loanType: "Personal Loan",
              statusUpdate: newStatus,
            });
          }
        } catch (emailError) {
          // Silent fail - email failure shouldn't block status update
        }
      }

      toast.success(
        `Loan application ${newStatus.replace("-", " ")} successfully!`,
      );
    } catch (error) {
      // Silent fail
      toast.error("Failed to update status. Please try again.");
    } finally {
      setUpdating(false);
    }
  };

  const handleDownloadDocuments = () => {
    if (!loan || !loan.documents) {
      toast.error("No documents available to download");
      return;
    }

    const docs = loan.documents;
    const urls = [
      { url: docs.aadharFront, name: "Aadhar_Front" },
      { url: docs.aadharBack, name: "Aadhar_Back" },
      { url: docs.panFront, name: "PAN_Front" },
      { url: docs.panBack, name: "PAN_Back" },
      { url: docs.form16, name: "Form_16" },
      { url: docs.salarySlip, name: "Salary_Slips_3_Months" },
      { url: docs.selfie, name: "Selfie" },
    ];

    urls.forEach(({ url, name }) => {
      if (url) {
        const link = document.createElement("a");
        link.href = url;
        link.download = `${loan.fullName}_${name}`;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    });

    toast.success("Documents download started. Check your downloads folder.");
  };

  const handleSendMessage = async () => {
    if (!subject.trim()) {
      toast.warning("Please enter a subject");
      return;
    }

    if (!message.trim()) {
      toast.warning("Please enter a message");
      return;
    }

    if (!loan.email) {
      toast.error("Customer email not found");
      return;
    }

    setSendingMessage(true);
    try {
      // Add email to Firebase mail collection for processing
      const mailRef = collection(db, "mail");
      await addDoc(mailRef, {
        to: loan.email,
        message: {
          subject: subject,
          text: message,
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
              <h2 style="color: #2563eb;">${subject}</h2>
              <p style="color: #374151; line-height: 1.6; white-space: pre-wrap;">${message}</p>
              <hr style="border: 1px solid #e5e7eb; margin: 20px 0;" />
              <p style="color: #6b7280; font-size: 12px;">This is an automated message from Aadhar Finance regarding your loan application.</p>
            </div>
          `,
        },
        createdAt: new Date(),
        sentBy: user.email,
        customerId: loan.uid || id,
        customerName: loan.fullName,
      });

      toast.success(`Email sent successfully to ${loan.fullName}!`);
      setMessage("");
      setSubject("");
      setMessageModal(false);
    } catch (error) {
      // Silent fail
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSendingMessage(false);
    }
  };

  // 30-minute time slots
  const TIME_SLOTS = [
    "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
    "12:00", "12:30", "13:00", "13:30", "14:00", "14:30",
    "15:00", "15:30", "16:00", "16:30", "17:00", "17:30",
  ];

  // Display slots in 12-hour AM/PM format (values stay 24h for date math)
  const formatSlotLabel = (slot) => {
    const [h, m] = slot.split(":").map(Number);
    const period = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 === 0 ? 12 : h % 12;
    return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
  };

  const handleScheduleCall = async () => {
    if (!callDate || !callTimeSlot) {
      toast.warning("Please select both date and time slot");
      return;
    }
    if (!loan.email) {
      toast.error("Customer email not found");
      return;
    }

    setSchedulingCall(true);
    try {
      const startDateTime = new Date(`${callDate}T${callTimeSlot}:00+05:30`);

      const scheduleFn = httpsCallable(functions, "scheduleVerificationCall");
      const result = await scheduleFn({
        customerEmail: loan.email,
        customerName: loan.fullName,
        dateTime: startDateTime.toISOString(),
        loanId: id,
        loanType: "Personal Loan",
        adminName: user.displayName || user.email,
      });

      await updateDoc(doc(db, "personalLoan", id), {
        verificationCall: {
          scheduled: true,
          scheduledDateTime: startDateTime.toISOString(),
          meetLink: result.data.meetLink,
          status: "scheduled",
          emailSent: true,
          scheduledBy: user.email,
          createdAt: new Date(),
          verificationCallId: result.data.verificationCallId,
        },
      });

      await addDoc(collection(db, "activities"), {
        loanId: id,
        loanType: "Personal Loan",
        action: "verification_call_scheduled",
        title: "Verification Call Scheduled",
        description: `Verification call scheduled for ${loan.fullName} on ${new Date(startDateTime).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })} at ${formatSlotLabel(callTimeSlot)}`,
        performedBy: user.email,
        performedByName: user.displayName || user.email,
        createdAt: new Date(),
        timestamp: new Date(),
      });

      setCallResult(result.data);
      await fetchLoan();
      toast.success("Verification call scheduled and email sent!");
    } catch (err) {
      toast.error(`Failed: ${err.message}`);
    } finally {
      setSchedulingCall(false);
    }
  };

  const handleDocumentsVerified = async () => {
    try {
      setUpdating(true);
      await updateDoc(doc(db, "personalLoan", id), {
        documentsVerified: true,
        documentsVerifiedAt: new Date(),
        documentsVerifiedBy: user.email || user.uid,
      });

      await addDoc(collection(db, "activities"), {
        loanId: id,
        loanType: "Personal Loan",
        action: "documents_verified",
        title: "Documents Verified",
        description: `Identity documents verified by ${user.displayName || user.email}`,
        performedBy: user.email,
        performedByName: user.displayName || user.email,
        createdAt: new Date(),
        timestamp: new Date(),
      });

      setLoan({
        ...loan,
        documentsVerified: true,
        documentsVerifiedAt: new Date(),
        documentsVerifiedBy: user.email || user.uid,
      });
      toast.success("Documents marked as verified!");
    } catch (err) {
      toast.error(`Failed: ${err.message}`);
    } finally {
      setUpdating(false);
    }
  };

  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link);
    setCopiedLink(true);
    toast.success("Meet link copied to clipboard!");
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const closeCallModal = () => {
    setShowCallModal(false);
    setCallDate("");
    setCallTimeSlot("");
    setCallResult(null);
    setCopiedLink(false);
  };

  if (!user) return null;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <svg
                className="animate-spin h-10 w-10 text-green-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
              </svg>
            </div>
            <FileText className="w-12 h-12 text-green-600 animate-pulse mx-auto mb-4" />
            <p className="text-gray-600">Loading loan application...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!loan) return null;

  // Loan offer summary values (simple interest calculation)
  const FIXED_MONTHLY_INTEREST_RATE = 2.0833;
  const tenureMonths = loan.tenure || loan.loanTenure;
  const monthlyInterestRate = loan.interestRate
    ? Number(loan.interestRate)
    : FIXED_MONTHLY_INTEREST_RATE;
  // Simple interest: charged on the full principal every month
  const monthlyInterest = Math.round(
    (Number(loan.loanAmount) * monthlyInterestRate) / 100
  );

  const InfoCard = ({ icon, label, value, fullWidth = false }) => {
    const Icon = icon;
    return (
      <div className={`${fullWidth ? "col-span-full" : ""}`}>
        <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
          <div className="p-2 bg-white rounded-lg shrink-0">
            <Icon className="w-5 h-5 text-gray-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
              {label}
            </p>
            <p className="text-sm font-semibold text-gray-900 wrap-break-word">
              {value || "Not provided"}
            </p>
          </div>
        </div>
      </div>
    );
  };

  // ===== Processing workflow helpers =====
  // The customer app creates personal loan applications with status "pending_review"
  // (older records may use "pending", "submitted" or no status at all). None of these
  // mean the admin has started the review yet, so they are all normalized to "pending".
  // Without this, step1Done (= !isPending) wrongly marks Step 1 "Review Application" as
  // Done the moment a customer submits, even though no admin action has been taken.
  const rawStatus = loan.status?.toLowerCase();
  const loanStatus =
    !rawStatus ||
    rawStatus === "pending" ||
    rawStatus === "pending_review" ||
    rawStatus === "pending-review" ||
    rawStatus === "submitted"
      ? "pending"
      : rawStatus;

  const StatusIcon = getStatusIcon(loanStatus);

  const isPending = loanStatus === "pending";
  const isUnderReview = loanStatus === "under-review";
  const isApproved = loanStatus === "approved";
  const isRejected = loanStatus === "rejected";
  const isDisbursed = loanStatus === "disbursed";
  const callScheduled = !!loan.verificationCall?.scheduled;

  // Strict step-by-step gating: a step unlocks only after the previous one is done
  const step1Done = !isPending;
  const step2Done = !!loan.documentsVerified;
  const step3Done = callScheduled;
  const step4Done = isApproved || isRejected || isDisbursed;
  const step5Done = isDisbursed;

  const step1Active = !step1Done && !isRejected;
  const step2Active = step1Done && !step2Done && !isRejected;
  const step3Active = step2Done && !step3Done && !isRejected;
  const step4Active = step3Done && !step4Done && !isRejected;
  const step5Active = step4Done && !step5Done && !isRejected;

  const stepState = (done, active) =>
    done ? "completed" : active ? "active" : "locked";

  // One row per workflow step: completed / active (current) / locked
  const WorkflowStep = ({ number, title, hint, state, children }) => (
    <div
      className={`flex items-start gap-4 p-4 rounded-xl border ${
        state === "completed"
          ? "bg-green-50 border-green-200"
          : state === "active"
          ? "bg-blue-50 border-blue-300 ring-1 ring-blue-200"
          : "bg-gray-50 border-gray-200 opacity-70"
      }`}
    >
      <div
        className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center text-sm font-bold ${
          state === "completed"
            ? "bg-green-600 text-white"
            : state === "active"
            ? "bg-blue-600 text-white"
            : "bg-gray-200 text-gray-500"
        }`}
      >
        {state === "completed" ? <CheckCircle className="w-5 h-5" /> : number}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p
            className={`text-sm font-semibold ${
              state === "completed"
                ? "text-green-800"
                : state === "active"
                ? "text-blue-900"
                : "text-gray-500"
            }`}
          >
            Step {number}: {title}
          </p>
          <span
            className={`text-[11px] font-semibold uppercase tracking-wide ${
              state === "completed"
                ? "text-green-600"
                : state === "active"
                ? "text-blue-600"
                : "text-gray-400"
            }`}
          >
            {state === "completed" ? "Done" : state === "active" ? "Current step" : "Locked"}
          </span>
        </div>
        <p className="text-xs text-gray-500 mt-0.5">{hint}</p>
        {state !== "locked" && children && (
          <div className="flex flex-wrap gap-2 mt-3">{children}</div>
        )}
      </div>
    </div>
  );
  // const calculateEMI = () => {
  //   if (!loan.loanAmount || !loan.interestRate || !loan.tenure) return 0;
  //   const P = loan.loanAmount;
  //   const r = loan.interestRate / 1200; // Monthly rate
  //   const n = loan.tenure;
  //   const emi = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  //   return Math.round(emi);
  // };

  return (
    <DashboardLayout>
      {/* Header with Back Button */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm font-medium">Back to Loan Applications</span>
        </button>

        <div
          className={`bg-white rounded-2xl shadow-sm border p-6 ${
            loan.instantApprovalFeePaid
              ? "border-yellow-300 ring-2 ring-yellow-200"
              : "border-gray-100"
          }`}
        >
          {loan.instantApprovalFeePaid && (
            <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-yellow-50 border border-yellow-200 rounded-xl">
              <IndianRupee className="w-5 h-5 text-yellow-600" />
              <div>
                <p className="text-sm font-bold text-yellow-800">
                  Instant Approval — Priority Case
                </p>
                <p className="text-xs text-yellow-700">
                  Fee Paid: ₹{loan.instantApprovalPayment?.amount || 0} • Order:{" "}
                  {loan.instantApprovalPayment?.orderId}
                </p>
              </div>
            </div>
          )}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center shrink-0">
                <FileText className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                  Personal Loan Application
                </h1>
                <p className="text-gray-500 text-sm mt-1">
                  Applied by {loan.fullName || "N/A"}
                </p>
                <div className="flex items-center gap-2 mt-3">
                  <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${getStatusColor(
                      loanStatus,
                    )}`}
                  >
                    <StatusIcon className="w-4 h-4" />
                    <span className="capitalize">
                      {loanStatus.replace("-", " ")}
                    </span>
                  </span>
                  {editingAmount ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={editedLoanAmount}
                        onChange={(e) => setEditedLoanAmount(e.target.value)}
                        className="w-32 px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      />

                      <button
                        onClick={handleSaveLoanAmount}
                        disabled={savingAmount}
                        className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-semibold hover:bg-green-700 disabled:opacity-50"
                      >
                        {savingAmount ? "Saving..." : "Save"}
                      </button>

                      <button
                        onClick={() => setEditingAmount(false)}
                        className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-xs font-semibold hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1.5 inline-flex text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        {formatCurrency(Number(loan.loanAmount))}
                      </span>

                      <button
                        onClick={() => {
                          setEditedLoanAmount(loan.loanAmount || "");
                          setEditingAmount(true);
                        }}
                        className="text-xs text-blue-600 hover:underline font-medium"
                      >
                        Edit
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Processing Workflow - guided step by step */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
        <h2 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-600" />
          Processing Workflow
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          Complete the steps in order. Only the current step is actionable — later steps stay locked until the earlier ones are done.
        </p>
        {isRejected && (
          <div className="mb-4 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm font-medium text-red-700">
            <XCircle className="w-4 h-4" />
            This application was rejected — the workflow is closed.
          </div>
        )}
        <div className="space-y-3">
          <WorkflowStep
            number={1}
            title="Review Application"
            state={stepState(step1Done, step1Active)}
            hint="Check applicant details and identity documents, then start the review."
          >
            {step1Active && (
              <button
                onClick={() => handleStatusUpdate("under-review")}
                disabled={updating}
                className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm font-medium"
              >
                <AlertCircle className="w-4 h-4" />
                Start Review
              </button>
            )}
          </WorkflowStep>

          <WorkflowStep
            number={2}
            title="Document Verification"
            state={stepState(step2Done, step2Active)}
            hint={
              loan.documentsVerified
                ? `Documents verified by ${loan.documentsVerifiedBy || "admin"}${
                    loan.documentsVerifiedAt ? ` on ${formatDate(loan.documentsVerifiedAt)}` : ""
                  }.`
                : "Open View Documents, check Aadhaar / PAN / bank documents, then confirm verification."
            }
          >
            {step2Active && (
              <button
                onClick={handleDocumentsVerified}
                disabled={updating}
                className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors disabled:opacity-50 text-sm font-medium"
              >
                <CheckCircle className="w-4 h-4" />
                Mark Documents Verified
              </button>
            )}
          </WorkflowStep>

          <WorkflowStep
            number={3}
            title="Verification Call"
            state={stepState(step3Done, step3Active)}
            hint={
              callScheduled
                ? "Verification call scheduled — details in the Verification Call card below."
                : "Schedule a video call to verify the customer's documents before deciding."
            }
          >
            {(step3Active || (step3Done && (isUnderReview || isApproved))) && (
              <button
                onClick={() => setShowCallModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                <Video className="w-4 h-4" />
                {callScheduled ? "Reschedule Call" : "Schedule Verification Call"}
              </button>
            )}
          </WorkflowStep>

          <WorkflowStep
            number={4}
            title="Final Decision"
            state={stepState(step4Done, step4Active)}
            hint={
              isRejected
                ? "Decision made: application rejected."
                : isApproved || isDisbursed
                ? "Decision made: application approved."
                : "Approve or reject the application after review and verification."
            }
          >
            {step4Active && (
              <>
                <button
                  onClick={() => handleStatusUpdate("approved")}
                  disabled={updating}
                  className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50 text-sm font-medium"
                >
                  <CheckCircle className="w-4 h-4" />
                  Approve
                </button>
                <button
                  onClick={() => handleStatusUpdate("rejected")}
                  disabled={updating}
                  className="flex items-center gap-2 px-4 py-2.5 bg-white text-red-600 border border-red-300 rounded-xl hover:bg-red-50 transition-colors disabled:opacity-50 text-sm font-medium"
                >
                  <XCircle className="w-4 h-4" />
                  Reject
                </button>
              </>
            )}
          </WorkflowStep>

          <WorkflowStep
            number={5}
            title="Disbursement"
            state={stepState(step5Done, step5Active)}
            hint={
              isRejected
                ? "Not applicable — application was rejected."
                : isDisbursed
                ? "Loan amount disbursed to the customer."
                : "Release the loan amount after approval."
            }
          >
            {step5Active && (
              <button
                onClick={() => handleStatusUpdate("disbursed")}
                disabled={updating}
                className="flex items-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors disabled:opacity-50 text-sm font-medium"
              >
                <DollarSign className="w-4 h-4" />
                Mark as Disbursed
              </button>
            )}
          </WorkflowStep>
        </div>
      </div>
 
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Loan Details */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-green-600" />
              Loan Details
            </h2>
            {/* Loan offer summary card: terms and arranger fee */}
            {(loan.loanAmount || tenureMonths || loan.arrangerFee) && (
              <div className="mb-5 bg-gray-50 rounded-2xl overflow-hidden border border-gray-100">
                <div className="p-5 pb-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Loan Amount</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatCurrency(Number(loan.loanAmount))}
                      </p>
                    </div>
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                      <Coins className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-3 mt-5">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Interest Rate</p>
                      <p className="text-lg font-bold text-gray-900">
                        {`${monthlyInterestRate}% p.m.`}
                      </p>
                    </div>
                    <div className="sm:text-center">
                      <p className="text-sm text-gray-500 mb-1">Tenure</p>
                      <p className="text-lg font-bold text-gray-900">
                        {tenureMonths ? `${tenureMonths} Months` : "N/A"}
                      </p>
                    </div>
                    <div className="sm:text-center">
                      <p className="text-sm text-gray-500 mb-1">
                        Monthly Interest
                      </p>
                      <p className="text-lg font-bold text-gray-900">
                        {monthlyInterest ? formatCurrency(monthlyInterest) : "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="px-5 py-3 bg-blue-50 flex items-center justify-between gap-3">
                  {loan.arrangerFee !== undefined &&
                  loan.arrangerFee !== null ? (
                    <p className="flex items-center gap-2 text-sm text-gray-600 min-w-0">
                      <Info className="w-4 h-4 text-blue-600 flex-shrink-0" />
                      <span className="truncate">
                        Arranger Fee: {formatCurrency(Number(loan.arrangerFee))}
                      </span>
                    </p>
                  ) : (
                    <span />
                  )}
                  <p className="flex items-center gap-2 text-sm text-gray-500 flex-shrink-0">
                    Powered by
                    <img
                      src={logo}
                      alt="Aadhar Finance"
                      className="h-5 w-auto object-contain"
                    />
                  </p>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InfoCard
                icon={IndianRupee}
                label="Loan Amount Requested"
                value={formatCurrency(Number(loan.loanAmount))}
              />
              <InfoCard
                icon={Clock}
                label="Loan Tenure"
                value={loan.tenure ? `${loan.tenure} months` : "N/A"}
              />
              <InfoCard
                icon={IndianRupee}
                label="Monthly Salary"
                value={formatCurrency(Number(loan.monthlySalary))}
              />
              <InfoCard icon={User} label="User ID" value={loan.userId} />
              <InfoCard
                icon={Calendar}
                label="Applied Date"
                value={formatDate(loan.createdAt)}
              />
            </div>
          </div>

          {/* Applicant Information */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-green-600" />
              Applicant Information
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InfoCard icon={User} label="Full Name" value={loan.fullName} />
              <InfoCard
                icon={Calendar}
                label="Date of Birth"
                value={loan.dateOfBirth}
              />
              <InfoCard icon={Mail} label="Email Address" value={loan.email} />
              <InfoCard
                icon={Phone}
                label="Mobile Number"
                value={loan.mobileNumber}
              />
              <InfoCard
                icon={User}
                label="Gender"
                value={
                  loan.gender
                    ? loan.gender.charAt(0).toUpperCase() + loan.gender.slice(1)
                    : "N/A"
                }
              />
              <InfoCard
                icon={IndianRupee}
                label="Monthly Salary"
                value={
                  loan.monthlySalary
                    ? formatCurrency(Number(loan.monthlySalary))
                    : "N/A"
                }
              />
            </div>
          </div>

          {/* Address Information */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-green-600" />
              Address Information
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InfoCard
                icon={Home}
                label="Complete Address"
                value={loan.completeAddress}
                fullWidth
              />
              <InfoCard icon={MapPin} label="City" value={loan.city} />
              <InfoCard icon={Globe} label="State" value={loan.state} />
              <InfoCard icon={Hash} label="Pincode" value={loan.pincode} />
            </div>
          </div>

          {/* Identity Documents */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-green-600" />
                Identity Documents
              </h2>
              <button
                onClick={() => setShowDocumentModal(true)}
                className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
              >
                <Eye className="w-4 h-4" />
                View Documents
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InfoCard
                icon={CreditCard}
                label="Aadhar Number"
                value={loan.aadharNumber}
              />
              <InfoCard
                icon={CreditCard}
                label="PAN Number"
                value={loan.panNumber}
              />
            </div>
            {(!loan.aadharNumber || !loan.panNumber) && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-xs text-yellow-800 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Some identity documents are missing. Request the applicant to
                  submit them.
                </p>
              </div>
            )}
          </div>

          {/* Bank Details */}
          {(loan.bankAccountNumber || loan.ifscCode) && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Building className="w-5 h-5 text-green-600" />
                Bank Details
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InfoCard
                  icon={Hash}
                  label="Account Number"
                  value={loan.bankAccountNumber}
                />
                <InfoCard
                  icon={FileText}
                  label="IFSC Code"
                  value={loan.ifscCode}
                />
              </div>
            </div>
          )}

          {/* Loan Purpose */}
          {loan.loanPurpose && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-green-600" />
                Loan Purpose
              </h2>
              <p className="text-sm text-gray-700 leading-relaxed">
                {loan.loanPurpose}
              </p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Application Timeline */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-green-600" />
              Timeline
            </h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-lg shrink-0">
                  <Calendar className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 uppercase">
                    Applied On
                  </p>
                  <p className="text-sm font-semibold text-gray-900 mt-1">
                    {formatDate(loan.createdAt)}
                  </p>
                </div>
              </div>

              {loan.updatedAt && (
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg shrink-0">
                    <Clock className="w-4 h-4 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-medium text-gray-500 uppercase">
                      Last Updated
                    </p>
                    <p className="text-sm font-semibold text-gray-900 mt-1">
                      {formatDate(loan.updatedAt)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              Quick Actions
            </h2>
            <div className="space-y-2">
              <button
                onClick={handleDownloadDocuments}
                className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <Download className="w-4 h-4 text-gray-500" />
                Download Documents
              </button>
              <button
                onClick={() => setMessageModal(true)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <MessageSquare className="w-4 h-4 text-gray-500" />
                Send Message
              </button>
              <button
                onClick={() => setShowActivityLog(true)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <History className="w-4 h-4 text-gray-500" />
                View Activity Log
              </button>
              <button
                onClick={() => setShowDocumentModal(true)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-xl transition-colors"
              >
                <Eye className="w-4 h-4 text-green-500" />
                View Documents
              </button>
            </div>
          </div>

          {/* eNACH Applications */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-green-600" />
              eNACH Applications
            </h2>
            {enachLoading ? (
              <p className="text-sm text-gray-500">Loading eNACH details...</p>
            ) : enachError ? (
              <p className="text-sm text-red-600">Unable to load eNACH applications.</p>
            ) : enachApplications.length === 0 ? (
              <p className="text-sm text-gray-500">No eNACH applications found.</p>
            ) : (
              <div className="space-y-4">
                {enachApplications.map((application) => {
                  const fields = Object.entries(application).filter(
                    ([key, value]) =>
                      key !== "id" &&
                      value != null &&
                      ["string", "number", "boolean"].includes(typeof value),
                  );

                  return (
                    <div
                      key={application.id}
                      className="border-b border-gray-100 last:border-0 last:pb-0 pb-4"
                    >
                      {fields.slice(0, 8).map(([key, value]) => (
                        <div key={key} className="flex justify-between gap-3 py-1.5">
                          <span className="text-sm text-gray-500">
                            {key.replace(/([A-Z])/g, " $1").replace(/[_-]/g, " ")}
                          </span>
                          <span className="text-sm font-medium text-gray-900 text-right break-all">
                            {typeof value === "boolean" ? (value ? "Yes" : "No") : String(value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Verification Call Info */}
          {loan.verificationCall?.scheduled && (
            <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Video className="w-5 h-5 text-blue-600" />
                Verification Call
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Status</span>
                  <span className="text-sm font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full capitalize">
                    {loan.verificationCall.status}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Date & Time</span>
                  <span className="text-sm font-medium">
                    {new Date(loan.verificationCall.scheduledDateTime).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })} {" "}
                    {new Date(loan.verificationCall.scheduledDateTime).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Email Sent</span>
                  <span className="text-sm font-medium">{loan.verificationCall.emailSent ? "Yes" : "No"}</span>
                </div>
                {loan.verificationCall.meetLink && (
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <span className="text-sm text-gray-700 truncate flex-1">{loan.verificationCall.meetLink}</span>
                      <button onClick={() => handleCopyLink(loan.verificationCall.meetLink)} className="p-1 hover:bg-gray-200 rounded">
                        {copiedLink ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-gray-500" />}
                      </button>
                      <a href={loan.verificationCall.meetLink} target="_blank" rel="noopener noreferrer" className="p-1 hover:bg-gray-200 rounded">
                        <ExternalLink className="w-4 h-4 text-blue-500" />
                      </a>
                    </div>
                    <button
                      onClick={() => window.open(loan.verificationCall.meetLink, "_blank")}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      <Video className="w-4 h-4" />
                      Join Meeting
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Applicant Summary */}
          <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl shadow-sm border border-green-100 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              Application Summary
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Loan Amount</span>
                <span className="text-sm font-bold text-gray-900">
                  {formatCurrency(Number(loan.loanAmount))}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Monthly Salary</span>
                <span className="text-sm font-bold text-gray-900">
                  {formatCurrency(Number(loan.monthlySalary))}
                </span>
              </div>
              <div className="border-t border-green-200 pt-3 mt-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-gray-900">
                    Loan to Income Ratio
                  </span>
                  <span className="text-lg font-bold text-green-600">
                    {loan.monthlySalary && loan.loanAmount
                      ? `${(
                          (Number(loan.loanAmount) /
                            (Number(loan.monthlySalary) * 12)) *
                          100
                        ).toFixed(1)}%`
                      : "N/A"}
                  </span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  Application Status
                </span>
                <span
                  className={`text-xs font-bold px-2 py-1 rounded-full capitalize ${getStatusColor(
                    loanStatus,
                  )}`}
                >
                  {loanStatus.replace("-", " ")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Document Preview Modal */}
      {showDocumentModal && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Document Preview
                </h3>
                <p className="text-sm text-gray-600 mt-0.5">
                  {loan.fullName || "N/A"}
                </p>
              </div>
              <button
                onClick={() => setShowDocumentModal(false)}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Aadhar Card Section */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CreditCard className="w-5 h-5 text-blue-600" />
                    <h4 className="font-semibold text-gray-900">Aadhar Card</h4>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {loan.aadharNumber || "N/A"}
                    </span>
                  </div>

                  {/* Aadhar Front */}
                  {loan.documents?.aadharFront ? (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-gray-600">
                        Front Side
                      </p>
                      {loan.documents.aadharFront
                        .toLowerCase()
                        .includes(".pdf") ? (
                        <a
                          href={loan.documents.aadharFront}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="h-48 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border-2 border-blue-200 hover:border-blue-500 transition-colors cursor-pointer flex flex-col items-center justify-center gap-3"
                        >
                          <FileText className="w-12 h-12 text-blue-600" />
                          <span className="text-sm font-medium text-blue-900">
                            View PDF
                          </span>
                        </a>
                      ) : (
                        <a
                          href={loan.documents.aadharFront}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <img
                            src={loan.documents.aadharFront}
                            alt="Aadhar Front"
                            className="w-full h-48 object-cover rounded-lg border-2 border-gray-200 hover:border-blue-500 transition-colors cursor-pointer"
                          />
                        </a>
                      )}
                    </div>
                  ) : (
                    <div className="h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                      <p className="text-gray-400 text-sm">
                        Front not available
                      </p>
                    </div>
                  )}

                  {/* Aadhar Back */}
                  {loan.documents?.aadharBack ? (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-gray-600">
                        Back Side
                      </p>
                      {loan.documents.aadharBack
                        .toLowerCase()
                        .includes(".pdf") ? (
                        <a
                          href={loan.documents.aadharBack}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="h-48 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border-2 border-blue-200 hover:border-blue-500 transition-colors cursor-pointer flex flex-col items-center justify-center gap-3"
                        >
                          <FileText className="w-12 h-12 text-blue-600" />
                          <span className="text-sm font-medium text-blue-900">
                            View PDF
                          </span>
                        </a>
                      ) : (
                        <a
                          href={loan.documents.aadharBack}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <img
                            src={loan.documents.aadharBack}
                            alt="Aadhar Back"
                            className="w-full h-48 object-cover rounded-lg border-2 border-gray-200 hover:border-blue-500 transition-colors cursor-pointer"
                          />
                        </a>
                      )}
                    </div>
                  ) : (
                    <div className="h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                      <p className="text-gray-400 text-sm">
                        Back not available
                      </p>
                    </div>
                  )}
                </div>

                {/* PAN Card Section */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CreditCard className="w-5 h-5 text-green-600" />
                    <h4 className="font-semibold text-gray-900">PAN Card</h4>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {loan.panNumber || "N/A"}
                    </span>
                  </div>

                  {/* PAN Front */}
                  {loan.documents?.panFront ? (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-gray-600">
                        Front Side
                      </p>
                      {loan.documents.panFront
                        .toLowerCase()
                        .includes(".pdf") ? (
                        <a
                          href={loan.documents.panFront}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="h-48 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border-2 border-green-200 hover:border-green-500 transition-colors cursor-pointer flex flex-col items-center justify-center gap-3"
                        >
                          <FileText className="w-12 h-12 text-green-600" />
                          <span className="text-sm font-medium text-green-900">
                            View PDF
                          </span>
                        </a>
                      ) : (
                        <a
                          href={loan.documents.panFront}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <img
                            src={loan.documents.panFront}
                            alt="PAN Front"
                            className="w-full h-48 object-cover rounded-lg border-2 border-gray-200 hover:border-green-500 transition-colors cursor-pointer"
                          />
                        </a>
                      )}
                    </div>
                  ) : (
                    <div className="h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                      <p className="text-gray-400 text-sm">
                        Front not available
                      </p>
                    </div>
                  )}

                  {/* PAN Back */}
                  {loan.documents?.panBack ? (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-gray-600">
                        Back Side
                      </p>
                      {loan.documents.panBack.toLowerCase().includes(".pdf") ? (
                        <a
                          href={loan.documents.panBack}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="h-48 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border-2 border-green-200 hover:border-green-500 transition-colors cursor-pointer flex flex-col items-center justify-center gap-3"
                        >
                          <FileText className="w-12 h-12 text-green-600" />
                          <span className="text-sm font-medium text-green-900">
                            View PDF
                          </span>
                        </a>
                      ) : (
                        <a
                          href={loan.documents.panBack}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <img
                            src={loan.documents.panBack}
                            alt="PAN Back"
                            className="w-full h-48 object-cover rounded-lg border-2 border-gray-200 hover:border-green-500 transition-colors cursor-pointer"
                          />
                        </a>
                      )}
                    </div>
                  ) : (
                    <div className="h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                      <p className="text-gray-400 text-sm">
                        Back not available
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Form 16, Salary Slips, and Selfie Section */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6 pt-6 border-t border-gray-200">
                {/* Form 16 */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="w-5 h-5 text-purple-600" />
                    <h4 className="font-semibold text-gray-900">Form 16</h4>
                  </div>

                  {loan.documents?.form16 ? (
                    <div className="space-y-2">
                      {loan.documents.form16.toLowerCase().includes(".pdf") ? (
                        <a
                          href={loan.documents.form16}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="h-64 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border-2 border-purple-200 hover:border-purple-500 transition-colors cursor-pointer flex flex-col items-center justify-center gap-3"
                        >
                          <FileText className="w-16 h-16 text-purple-600" />
                          <span className="text-sm font-medium text-purple-900">
                            View PDF
                          </span>
                        </a>
                      ) : (
                        <a
                          href={loan.documents.form16}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <img
                            src={loan.documents.form16}
                            alt="Form 16"
                            className="w-full h-64 object-cover rounded-lg border-2 border-gray-200 hover:border-purple-500 transition-colors cursor-pointer"
                          />
                        </a>
                      )}
                    </div>
                  ) : (
                    <div className="h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                      <p className="text-gray-400 text-sm">
                        Form 16 not available
                      </p>
                    </div>
                  )}
                </div>

                {/* Salary Slips (3 Months) */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="w-5 h-5 text-orange-600" />
                    <h4 className="font-semibold text-gray-900">
                      Salary Slips (3 Months)
                    </h4>
                  </div>

                  {loan.documents?.salarySlip ? (
                    <div className="space-y-2">
                      {loan.documents.salarySlip
                        .toLowerCase()
                        .includes(".pdf") ? (
                        <a
                          href={loan.documents.salarySlip}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="h-64 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg border-2 border-orange-200 hover:border-orange-500 transition-colors cursor-pointer flex flex-col items-center justify-center gap-3"
                        >
                          <FileText className="w-16 h-16 text-orange-600" />
                          <span className="text-sm font-medium text-orange-900">
                            View PDF
                          </span>
                        </a>
                      ) : (
                        <a
                          href={loan.documents.salarySlip}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <img
                            src={loan.documents.salarySlip}
                            alt="Salary Slips"
                            className="w-full h-64 object-cover rounded-lg border-2 border-gray-200 hover:border-orange-500 transition-colors cursor-pointer"
                          />
                        </a>
                      )}
                    </div>
                  ) : (
                    <div className="h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                      <p className="text-gray-400 text-sm">
                        Salary slips not available
                      </p>
                    </div>
                  )}
                </div>

                {/* Selfie */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-3">
                    <User className="w-5 h-5 text-indigo-600" />
                    <h4 className="font-semibold text-gray-900">Selfie</h4>
                  </div>

                  {loan.documents?.selfie ? (
                    <div className="space-y-2">
                      {loan.documents.selfie.toLowerCase().includes(".pdf") ? (
                        <a
                          href={loan.documents.selfie}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="h-64 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg border-2 border-indigo-200 hover:border-indigo-500 transition-colors cursor-pointer flex flex-col items-center justify-center gap-3"
                        >
                          <FileText className="w-16 h-16 text-indigo-600" />
                          <span className="text-sm font-medium text-indigo-900">
                            View PDF
                          </span>
                        </a>
                      ) : (
                        <a
                          href={loan.documents.selfie}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <img
                            src={loan.documents.selfie}
                            alt="Selfie"
                            className="w-full h-64 object-cover rounded-lg border-2 border-gray-200 hover:border-indigo-500 transition-colors cursor-pointer"
                          />
                        </a>
                      )}
                    </div>
                  ) : (
                    <div className="h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                      <p className="text-gray-400 text-sm">
                        Selfie not available
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Info */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h5 className="font-semibold text-sm text-gray-900 mb-3">
                  Additional Information
                </h5>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-xs">
                  <div>
                    <p className="text-gray-500">Email</p>
                    <p className="font-medium text-gray-900">
                      {loan.email || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Mobile</p>
                    <p className="font-medium text-gray-900">
                      {loan.mobileNumber || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Date of Birth</p>
                    <p className="font-medium text-gray-900">
                      {loan.dateOfBirth || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Gender</p>
                    <p className="font-medium text-gray-900 capitalize">
                      {loan.gender || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Bank Account</p>
                    <p className="font-medium text-gray-900">
                      {loan.bankAccountNumber || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">IFSC Code</p>
                    <p className="font-medium text-gray-900">
                      {loan.ifscCode || "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowDocumentModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
              >
                Close
              </button>
              <button
                onClick={handleDownloadDocuments}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message Modal */}
      {messageModal && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">Send Message</h3>
              <button
                onClick={() => setMessageModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                disabled={sendingMessage}
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Send to: {loan.fullName}
                </label>
                <p className="text-xs text-gray-500">
                  {loan.email || "No email available"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Enter email subject..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  disabled={sendingMessage}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your message here..."
                  className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                  disabled={sendingMessage}
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200">
              <button
                onClick={() => setMessageModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={sendingMessage}
              >
                Cancel
              </button>
              <button
                onClick={handleSendMessage}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                disabled={sendingMessage}
              >
                {sendingMessage && (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                )}
                {sendingMessage ? "Sending..." : "Send Message"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activity Log Modal */}
      {showActivityLog && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">Activity Log</h3>
              <button
                onClick={() => setShowActivityLog(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]">
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <FileText className="w-4 h-4 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      Application Submitted
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatDate(loan.createdAt)}
                    </p>
                  </div>
                </div>
                {loan.updatedAt && (
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Clock className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        Status Updated
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Updated to:{" "}
                        <span className="font-semibold capitalize">
                          {loan.status?.replace("-", " ")}
                        </span>
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDate(loan.updatedAt)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end p-4 border-t border-gray-200">
              <button
                onClick={() => setShowActivityLog(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmModal.show && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-scale-in">
            <div className="p-6">
              <div className="flex items-center justify-center w-16 h-16 mx-auto bg-blue-100 rounded-full mb-4">
                <AlertCircle className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
                Confirm Action
              </h3>
              <p className="text-gray-600 text-center mb-6">
                Are you sure you want to{" "}
                <span className="font-semibold">{confirmModal.action}</span>?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() =>
                    setConfirmModal({ show: false, action: "", status: "" })
                  }
                  className="flex-1 px-4 py-2.5 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmStatusUpdate}
                  disabled={updating}
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium disabled:opacity-50"
                >
                  {updating ? "Processing..." : "OK"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Verification Call Modal */}
      {showCallModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Video className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {callResult ? "Call Scheduled!" : "Schedule Verification Call"}
                    </h3>
                    <p className="text-sm text-gray-500">{loan.fullName}</p>
                  </div>
                </div>
                <button onClick={closeCallModal} className="p-2 hover:bg-gray-100 rounded-full">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {callResult ? (
                <div>
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="font-semibold text-green-800">Successfully Scheduled!</span>
                    </div>
                    <p className="text-sm text-green-700">Email with meeting link sent to {loan.email}</p>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div>
                      <label className="block text-sm text-gray-500 mb-1">Date</label>
                      <p className="font-medium text-gray-900">
                        {new Date(callResult.scheduledDateTime || `${callDate}T${callTimeSlot}:00+05:30`).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-500 mb-1">Time</label>
                      <p className="font-medium text-gray-900">
                        {new Date(callResult.scheduledDateTime || `${callDate}T${callTimeSlot}:00+05:30`).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })}
                      </p>
                    </div>
                    {callResult.meetLink && (
                      <div>
                        <label className="block text-sm text-gray-500 mb-1">Google Meet Link</label>
                        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <span className="text-sm text-gray-700 truncate flex-1">{callResult.meetLink}</span>
                          <button onClick={() => handleCopyLink(callResult.meetLink)} className="p-1 hover:bg-gray-200 rounded">
                            {copiedLink ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-gray-500" />}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {callResult.meetLink && (
                    <div className="flex gap-2 mb-4">
                      <button
                        onClick={() => handleCopyLink(callResult.meetLink)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-medium transition-colors"
                      >
                        {copiedLink ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        {copiedLink ? "Copied!" : "Copy Link"}
                      </button>
                      {navigator.share && (
                        <button
                          onClick={() => navigator.share({ title: "Verification Call", text: `Join: ${callResult.meetLink}`, url: callResult.meetLink })}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-medium transition-colors"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Share
                        </button>
                      )}
                    </div>
                  )}

                  <button onClick={closeCallModal} className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors">
                    Done
                  </button>
                </div>
              ) : (
                <div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select Date</label>
                    <input
                      type="date"
                      value={callDate}
                      onChange={(e) => setCallDate(e.target.value)}
                      min={new Date().toISOString().split("T")[0]}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select Time Slot (30 min)</label>
                    <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                      {TIME_SLOTS.map((slot) => (
                        <button
                          key={slot}
                          onClick={() => setCallTimeSlot(slot)}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${callTimeSlot === slot ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                        >
                          {formatSlotLabel(slot)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 mb-6">
                    <p className="text-sm text-blue-700">
                      A Google Meet link will be created and emailed to <strong>{loan.email}</strong>
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <button onClick={closeCallModal} className="flex-1 px-4 py-2.5 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors font-medium">
                      Cancel
                    </button>
                    <button
                      onClick={handleScheduleCall}
                      disabled={schedulingCall || !callDate || !callTimeSlot}
                      className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {schedulingCall ? (
                        <span className="flex items-center justify-center gap-2">
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Scheduling...
                        </span>
                      ) : (
                        "Schedule & Send"
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
